import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import useTheme from "../../../../common/hooks/useTheme";
import styled from "styled-components";
import {
  BaseStyledOutlinedInput,
  BaseStyledInputLabel,
  LABEL_TRANSFORM,
  BaseInputStyleProps,
} from "./BaseInputStyles";

interface NumericFormatCustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  /** Also accept a typed "," as the decimal key (rendered/emitted as "."). */
  acceptCommaDecimal?: boolean;
  /** Max decimals that can be typed; 0 refuses the decimal point entirely. */
  decimalScale?: number;
}

interface NumericInputStyleProps extends BaseInputStyleProps {
  has_unit?: boolean;
}

// Extend BaseStyledOutlinedInput to handle unit display
const StyledNumericInput = styled(
  BaseStyledOutlinedInput
)<NumericInputStyleProps>(({ has_unit }) => ({
  ...(has_unit && {
    "& .MuiOutlinedInput-input": {
      width: "30%", // Leave space for unit
    },
  }),
}));

const StyledFormHelperText = styled(FormHelperText)`
  width: 500px;
  font-family: var(--font-Medium), sans-serif;
  font-size: 10px;
  position: absolute;
  bottom: -24px;
  left: -16px;
  background-color: transparent;
  color: ${(props) => props.theme.colors_ds.DANGER_GRAPH_DEFICIT};
`;

const NumericFormatAdapter = React.forwardRef<
  HTMLInputElement,
  NumericFormatCustomProps & Partial<NumericFormatProps>
>(function NumericFormatAdapter(props, ref) {
  const {
    onChange,
    name,
    allowNegative = true,
    acceptCommaDecimal = false,
    decimalScale,
    ...other
  } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: name,
            value: values.value,
          },
        });
      }}
      thousandSeparator={false}
      decimalSeparator="."
      // Opt-in: the value is always shown and emitted with ".", but a typed
      // "," is taken as the decimal key too (European keyboards / numpads).
      allowedDecimalSeparators={acceptCommaDecimal ? [".", ","] : undefined}
      decimalScale={decimalScale}
      allowNegative={allowNegative}
      valueIsNumericString={true}
    />
  );
});

const NumericInputField = ({
  label,
  placeholder,
  unit,
  handleChange,
  value,
  error,
  setFocusedIndex = () => {},
  name = "",
  disabled = false,
  allowNegative = true,
  acceptCommaDecimal = false,
  decimalScale,
}: {
  label?: string;
  placeholder?: string;
  unit?: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  error?: string;
  setFocusedIndex?: (index: string | null) => void;
  name?: string;
  disabled?: boolean;
  allowNegative?: boolean;
  /** Also accept a typed "," as the decimal key; shown and emitted as ".". */
  acceptCommaDecimal?: boolean;
  /** Max decimals that can be typed; 0 refuses the decimal point entirely. */
  decimalScale?: number;
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fix cursor position when value is "0" after clearing the field
  useEffect(() => {
    if (focused && value === "0" && inputRef.current) {
      // Set cursor to the end of the input (after "0")
      const input = inputRef.current;
      setTimeout(() => {
        input.setSelectionRange(1, 1);
      }, 0);
    }
  }, [value, focused]);

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        width: "100%",
      }}
    >
      <FormControl
        variant="outlined"
        fullWidth
        sx={{
          position: "relative",
          height: "56px",
          minHeight: "56px",
        }}
      >
        {label && (
          <BaseStyledInputLabel
            htmlFor={label}
            theme={theme}
            style={{
              transform: Boolean(value || focused || placeholder)
                ? LABEL_TRANSFORM.shrunk
                : LABEL_TRANSFORM.normal,
            }}
          >
            {label}
          </BaseStyledInputLabel>
        )}

        <StyledNumericInput
          id={label}
          inputRef={inputRef}
          value={value || ""}
          placeholder={placeholder}
          disabled={disabled}
          inputComponent={NumericFormatAdapter as any}
          inputProps={{
            name,
            allowNegative,
            acceptCommaDecimal,
            decimalScale,
          }}
          onFocus={() => {
            setFocused(true);
            setFocusedIndex(name);
          }}
          onBlur={() => {
            setFocused(false);
            setFocusedIndex(null);
          }}
          onChange={handleChange as any}
          theme={theme}
          has_error={error ? "true" : undefined}
          has_unit={Boolean(unit)}
          endAdornment={
            unit && (value || focused) ? (
              <InputAdornment
                position="end"
                sx={{ marginTop: "16px", marginLeft: "8px" }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-Medium)",
                    fontSize: "14px",
                    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                  }}
                >
                  {unit}
                </span>
              </InputAdornment>
            ) : null
          }
        />

        {error && error !== "error" && (
          <StyledFormHelperText theme={theme}>{error}</StyledFormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default NumericInputField;
